import { debounce } from "lodash.throttle";

import { ConditionParam, ConditionParams } from "@multiweb/clients/dataReader";
import { ISectionLogger, ISectionLoggerFactory, LoggingSection } from "@multiweb/logging";
import {
    Aggregation,
    IAttrValue,
    IOWClassAttributeDescription,
    IOWEnumAttributeDescription,
    IResultMessage,
    IRunMethodResult,
} from "@multiweb/types/contracts";
import { RequestContext } from "@multiweb/types/multiweb";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { ErrorHandler } from "UI/Errors";
import { ObjectContext } from "UI/UI/UI/UIManagerInterfaces";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { ObjectSignatureSerializer } from "../../../Cryptography/Crypto.OW/ObjectSignatureSerializer";
import { DataConverter } from "../../../Global/Base/Converters/DataConverter";
import { ISignatureTemplate } from "../../../Global/Base/Crypto/ISignatureInfo";
import { CaseInsensitiveMap } from "../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { EncAttrValue } from "../../../Global/Base/DataTypes/EncAttrValue";
import { IEncAttrValue } from "../../../Global/Base/DataTypes/IAttrValue";
import { DIContainer } from "../../../Global/Base/DIContainer";
import { Event } from "../../../Global/Base/Events";
import { InvalidArgumentException, InvalidOperationException } from "../../../Global/Base/Exceptions";
import { IComparable } from "../../../Global/Base/IComparable";
import { IDisposable } from "../../../Global/Base/IDisposable";
import { AsyncLock } from "../../../Global/Base/Promises/AsyncLock";
import { CancellationToken, CancellationTokenSource } from "../../../Global/Base/Promises/CancellationTokenSource";
import { PromiseUtils } from "../../../Global/Base/Promises/PromiseUtils";
import { SecurityUtils } from "../../../Global/Base/Security/SecurityUtils";
import { DateUtils } from "../../../Global/Base/Utils/DateUtils";
import { ValidationErrorType } from "../../Basic/EOWExceptionsInterfaces";
import { EOWException, EOWRightEvaluationException } from "../../Basic/OWExceptions";
import { AttrValueReader } from "../AttrValueReader";
import { AttributeDescription, BasicDataType, IDataTypeSerialization } from "../Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "../Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { InstanceManager } from "../InstanceManager";
import { IWorkflowMenuItem } from "../IWorkflowManager";
import { OWClassDescription } from "../OWDescriptions";
import { OWManager } from "../OWManager";
import {
    IAttrDataForTransfer,
    IImplicitFormInfo,
    IOWObjectForTransfer,
    IOWObjectJsonPatchDTO,
    RunMethodBaseParams,
    RunMethodWithParamsBaseParams,
} from "../OWManagerInterfaces";
import { PasswordManager } from "../PasswordManager";
import { ClassItemRightTypes, ClassItemType, IClassItemRights } from "../Rights";
import { SerializeFuncs } from "../Serializers";
import { SerializerParams } from "../types";
import { Enum } from "./Enums";
import { IChangedAttr, IOWObject } from "./IOWObject";
import { IValidationDataSimple } from "./IValidationDataObject";
import { OWObjectList } from "./OWObjectList";
import { HasPositiveID } from "./utils";

/**
 * Třída OWObject reprezentuje aplikační záznam v klientském prostředí.
 * Obsahuje jeho atributy, umožňuje číst a modifikovat jejich hodnoty a umožňuje spouštět jeho metody.
 * @includeToDoc
 */
export class OWObject implements IOWObject {
    private _owObjectLogger: ISectionLogger;
    private _encryptionLogger: ISectionLogger;

    //#region "Fields"
    private _changeHistory: IOWObjectJsonPatchDTO[] = [];
    private _historyPosition: number = -1;

    private _id?: string;
    /**
    * Chain promisů s probihajicimi aktualizacemi dat
    */
    private _waitForUpdatesTask: Promise<void> = Promise.resolve();

    private _itemRightsCache: Map<string, CachedRightItem<IClassItemRights>> = new Map();
    private _conditionEvaluationsCache: Map<string, CachedRightItem<boolean>> = new Map();

    /**
     * Unikátní identifikátor aplikačního záznamu
     */
    public get id(): string | undefined {
        if (this.isNew)
            return this.instanceID;

        return this._id;
    }
    protected _objectName: string | undefined = undefined;
    public get objectName(): string | undefined {
        return this._objectName;
    }
    protected _instanceID: string | undefined = undefined;
    public get instanceID(): string | undefined {
        return this._instanceID;
    }

    protected _objectIdentity: string | undefined = undefined;
    public get objectIdentity(): string | undefined {
        return this._objectIdentity;
    }

    private _isServerSideEncrypted: boolean | undefined = undefined;
    public get isServerSideEncrypted(): boolean | undefined {
        return this._isServerSideEncrypted;
    }

    private _isEncryptionFinalized: boolean | undefined = undefined;
    public get isEncryptionFinalized(): boolean | undefined {
        return this._isEncryptionFinalized;
    }

    private _isClientSideEncrypted: boolean | undefined = undefined;
    public get isClientSideEncrypted(): boolean | undefined {
        return this._isClientSideEncrypted;
    }

    private _canEdit: boolean = false;
    private _canDelete: boolean = false;

    public get canEdit(): boolean {
        return this._canEdit;
    }
    public get canDelete(): boolean {
        return this._canDelete;
    }
    public canExecuteActions: boolean = false;

    private _createAttrChangeCycle: number;
    /**
     * Čitač změn atributů na serverové instanci
     */
    private _attrChangeCycle: number;

    private _roleIds: string[];
    private _editRoleIds: string[];
    private _deleteRoleIds: string[];

    public get roleIds(): string[] {
        return this._roleIds;
    }
    public get editRoleIds(): string[] {
        return this._editRoleIds;
    }
    public get deleteRoleIds(): string[] {
        return this._deleteRoleIds;
    }

    /**
     * Zámky pro načítání jednotlivých vazeb aby více dotazů spouštělo vždy jen jedno načítání
     */
    private _associationLoadingLocks: CaseInsensitiveMap<AsyncLock> = new CaseInsensitiveMap();

    public readonly valueSet: Event<IOWObject, IChangedAttr> = new Event<IOWObject, IChangedAttr>();
    public readonly attributeChanged: Event<OWObject, string> = new Event<OWObject, string>();
    public readonly attributesChanged: Event<OWObject, string[]> = new Event<OWObject, string[]>();
    public readonly relationAttributeChanged: Event<OWObject, string> = new Event<OWObject, string>();
    public readonly newSaved: Event<OWObject, void> = new Event<OWObject, void>();
    public readonly beforeSave: Event<OWObject, void> = new Event<OWObject, void>();
    public readonly saved: Event<OWObject, void> = new Event<OWObject, void>();
    public readonly validityChange: Event<OWObject, boolean> = new Event<OWObject, boolean>();
    public readonly dataRefreshed: Event<OWObject, boolean> = new Event<OWObject, boolean>();
    public readonly objectDecrypted: Event<OWObject, undefined> = new Event<OWObject, undefined>();
    public readonly disposed: Event<OWObject, undefined> = new Event<OWObject, undefined>();
    public readonly deleted: Event<OWObject, undefined> = new Event<OWObject, undefined>();

    public get isNew(): boolean {
        return this._id == null;
    }
    public isChanged: boolean = false;
    public isTempObject: boolean = false;
    public get isCopy(): boolean {
        return this._isCopy;
    }
    public get isVersioned(): boolean {
        return this._isVersioned;
    }

    public get WorkflowID(): number | null {
        return this._workflowId;
    }

    private _workflowId: number | null = null;
    private _isVersioned: boolean = false;
    private _isCopy: boolean = false;

    private __parentObject?: OWObject | null;
    /**
     * Rodičovský objekt, přes jehož vazbu byl tento dočten.
     * Hodnota null znamená, že je to hlavní objekt.
     * Hodnota undefined znamená, že proměnná ještě nebyla nastavena.
     */
    private get _parentObject(): OWObject | null | undefined {
        if (this.__parentObject && !this.__parentObject.isDisposed)
            return this.__parentObject;
        return undefined;
    }
    private set _parentObject(v: OWObject | null) {
        this.__parentObject = v;
    }

    private _attributeOnParentObject: string;

    private _classInfo: OWClassDescription | undefined = undefined;
    public get classInfo(): OWClassDescription | undefined {
        return this._classInfo;
    }

    //pole atributu cekajicich na provedeni updateInstance ->
    private _updatingAttrs: IRunningAttrUpdate[] = [];
    //private _pendingAttrsForUpdate: Map<string, Deferred<IAttrValue>> = new Map();
    /**
     * Zámek probíhajícího refreshe objektu
     */
    private _runningRefreshLock: AsyncLock = new AsyncLock();
    private _disposed: boolean = false;
    public get isDisposed(): boolean {
        return this._disposed;
    }
    private _values: CaseInsensitiveMap<IAttrValue> = new CaseInsensitiveMap<IAttrValue>();
    /**
     * Dočasně udržuje hodnoty atributů, které budou zašifrovány.
     * Hodnoty se do _values nastaví po přijetí odpovědi na SetValue ze serveru.
     */
    private _tempDecryptedValues: CaseInsensitiveMap<IAttrValue> = new CaseInsensitiveMap<IAttrValue>();
    // TODO: docasne z duvodu toho ze na elementech zalezi na velikosti pismen pri posilani atributu, az se to vyladi tak odstranit
    private _ciAttrName: CaseInsensitiveMap<string> = new CaseInsensitiveMap<string>();

    private _tempObjectIDs: string[];

    //private _tempObjectList: OWObjectList = new OWObjectList(null);

    private _validationErrors: IValidationDataSimple[] = [];

    private _encryptionKey: CryptoKey;

    /** Informace o tom, zda je object zverejneny na rozhrani sluzby */
    private _isPublicObject: boolean = false;

    /**
     * Informace zda je objekt ve validním stavu
     */
    public get isValid(): boolean {
        return this._validationErrors.length === 0;
    }
    //#endregion "Fields"
    private readonly _reloadDebounced: () => Promise<void>;
    private readonly _context: ObjectContext;

    constructor(context: ObjectContext | undefined) {
        this._owObjectLogger = DIContainer.instance.create(ISectionLoggerFactory).create(LoggingSection.objectWrapper);
        this._encryptionLogger = DIContainer.instance.create(ISectionLoggerFactory).create(LoggingSection.encryption);
        this._reloadDebounced = debounce(async () => await this._reload(), 100, { leading: true, trailing: false });
        this._context = context;
    }

    public async getSerializers(ai: AttributeDescription, params?: SerializerParams): Promise<IDataTypeSerialization> {
        return SerializeFuncs.getTypeSerializers(ai.type, (ai as IOWEnumAttributeDescription).enumName, params);
    }

    /**
     * Šifrovací klíč k této instanci objektu
     */
    public async getEncryptionKey(cancellationToken: CancellationToken = CancellationToken.none): Promise<CryptoKey> {
        if (this._encryptionKey == null) {
            // @ts-ignore
            // eslint-disable-next-line unused-imports/no-unused-vars
            const [salted, encryptionKeyBase] = await this._getEncryptionKey(cancellationToken);

            if (encryptionKeyBase == null)
                return null;

            /*if (!salted) this._encryptionKey = await SecurityUtils.derivePasswordToAES256Key(encryptionKeyBase, await this._getObjectID());
            else*/ this._encryptionKey = encryptionKeyBase;
        }
        return this._encryptionKey;
    }

    private async _getEncryptionKey(cancellationToken: CancellationToken = CancellationToken.none): Promise<[boolean, CryptoKey]> {
        if (this._encryptionKey) return [true, this._encryptionKey];
        const encryptionKeyBase: CryptoKey = this.isNew ? PasswordManager.getTempPassword(this.instanceID) : PasswordManager.getPassword(this.objectName, this.id);
        if (encryptionKeyBase)
            return [false, encryptionKeyBase];
        if (this.classInfo && this.classInfo.passwordMasterLink) {
            const obj = await this._getValue(this.classInfo.passwordMasterLink, true, cancellationToken);
            if (!(obj instanceof NullOWObject) && obj && obj instanceof IOWObject)
                return await (obj as unknown as OWObject)._getEncryptionKey(cancellationToken);
        }
        return [false, null];
    }

    /**
     * Zda je dostupné uživatelem zadané heslo pro šifrování a dešifrování tohoto aplikačního záznamu
     */
    public get hasEncryptionPassword(): boolean {
        return this.isNew ? PasswordManager.hasTempPassword(this.instanceID) : PasswordManager.hasPassword(this.objectName, this.id);
    }

    /**
     * Název celé vazby, ve které je umístěn tento objekt na nejvyšším rodičovském záznamu
     */
    public get attributeOnParentObject(): string {
        return this._attributeOnParentObject;
    }

    /**
     * Vrací kolekci všech validačních chyb, prázdnou, pokud žádné neexistují
     */
    public getValidationErrors(): IValidationDataSimple[] {
        return this._validationErrors.slice(); // shallow copy
    }

    /**
     * Přidá novou validační chybu
     * @param error Informace o validační chybě
     */
    public addError(error: IValidationDataSimple): void {
        const isFirstValidationError = error.error.type !== ValidationErrorType.recommendedError && !this._validationErrors.some((e) => e.error.type !== ValidationErrorType.recommendedError);

        this._validationErrors.push(error);
        //prvni validacni chyba, ktera znemoznuje ulozeni (nepatri zde recommended)
        if (isFirstValidationError)
            this.validityChange.trigger(this, false);
    }

    /**
     * Odstraní validační chybu
     * @param errorValidační chyba k odstranění
     */
    public removeError(error: IValidationDataSimple): void {
        const pos = this._validationErrors.findIndex((e) => e === error);
        if (pos < 0)
            return;

        this._validationErrors.splice(pos, 1);
        if (this.isValid)
            this.validityChange.trigger(this, true);
    }

    /**
     * Odstraní všechny validační chyby
     */
    public clearErrors(): void {
        const isValid = this.isValid;
        this._validationErrors = [];
        if (!isValid)
            this.validityChange.trigger(this, true);
    }

    public get isInstanceUpdateInProgress(): boolean {
        return this._updatingAttrs.length > 0;
    }

    /**
     * Vrací hodnotu atributu ObjectID bez čekání na update
     */
    // @ts-ignore
    private async _getObjectID(): Promise<string> {
        return AttrValueReader.read(await this._getValue("ObjectID", true));
    }

    /**
     * Vytvoří instanci OWObject
     * @param objectData Serializovaná data objektu
     * @param isPublicObject
     * @param context Kontext framu. Dá se předpokládat, že objekt vytvořený na jednom framu bude mít stejný kontext i pokud bude předáván do jiných framů. Role formuláře se nezmění. Stejně tak by se neměl změnit ani editační kontext
     * @param initFunc Inicializační token
     * @param cancellationToken Token pro zrušení
     */

    public static createObject(objectData: IOWObjectForTransfer, isPublicObject: boolean = false, context: ObjectContext, initFunc?: (obj: OWObject) => void, cancellationToken: CancellationToken = CancellationToken.none): Promise<OWObject> {
        const existingInstance = InstanceManager.getInstance(objectData.InstanceID);
        if (existingInstance)
            return Promise.resolve(existingInstance);

        return ModelDescriptionsManager.instance.getClassInfo(objectData.ClassName).then(async (classInfo: OWClassDescription) => {
            const obj = new OWObject(context);
            obj._isPublicObject = isPublicObject;
            if (isPublicObject)
                obj._parentObject = null; // null znamená, že to je hlavní objekt (oproti undefined, které znamená, že to ještě nebylo nastaveno)
            obj._classInfo = classInfo;
            obj._objectName = objectData.ClassName;
            obj._instanceID = objectData.InstanceID;
            obj._isVersioned = objectData.isVersioned ?? false;
            obj._isCopy = objectData.isCopy ?? false;
            obj._workflowId = objectData.WorkflowID;
            // načte ID a ObjectID, které je později potřeba
            await obj._loadSpecificAttributes(objectData, cancellationToken, "ID", "ObjectID", obj.classInfo.passwordMasterLink);

            if (initFunc)
                initFunc(obj);

            InstanceManager.addObject(obj); // potřebujeme mít vyplněné id

            await obj._loadData(objectData, cancellationToken, undefined, obj.isNew);

            return obj;
        });
    }

    public async refresh(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        await this.waitForPendingUpdates();

        const releaser = await this._runningRefreshLock.lock();
        try {
            await this._publishIfNecessary(cancellationToken);
            const data = await OWManager.getInstanceData(this.instanceID, true, this._attrChangeCycle, this._getRequestContext(cancellationToken));
            await this._loadData(data, cancellationToken);
        } finally {
            releaser.release();
        }

        this.dataRefreshed.trigger(this, false);
    }

    public async reload(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        //zavolá funkci jako debounced - seskupí opakované volání v rámci časového intervalu do jednoho. (viz. FunctionUtils.debouncedFunction)
        if (cancellationToken === CancellationToken.none)
            await this._reloadDebounced();
        else //pokud je zadán cancellation token, nelze funkci seskupit. Token nemusí být stejný.
            await this._reload();
    }

    private _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this._context, cancellationToken);
    }

    private async _reload(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        if (this._disposed) {
            this._owObjectLogger.warn(`Trying to reload already disposed instance '${this.instanceID}' for object '${this.objectName}: ${this.id}'`); //`
            return;
        }
        await this._publishIfNecessary(cancellationToken);

        const data = await OWManager.reloadInstance(this.instanceID, false, this._getRequestContext(cancellationToken));
        this._disposeAssociations();
        await this._loadData(data, cancellationToken);
        this.dataRefreshed.trigger(this, true);
    }

    /**
     * Pokud současná instance ještě není vypublikovaná, tak ji vypublikuje
     * @param requestContext Viz {@link RequestContext}
     */
    private async _publishIfNecessary(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        if (this._isPublicObject)
            return;

        const publicObjectPath = OWObject._getFullMasterObjAttrName(this);

        await OWManager.publishInstance(publicObjectPath.masterObject.instanceID, publicObjectPath.fullAttrName, this._getRequestContext(cancellationToken));
        this._isPublicObject = true;
    }

    public waitForPendingUpdates(): Promise<void> {
        return this._waitForUpdatesTask;
    }

    public enqueuePendingUpdateTask(task: Promise<void>): Promise<void> {
        return this._waitForUpdatesTask = this._waitForUpdatesTask.then(() => task);
    }

    /**
     * Dešifruje objekt a vrací informaci o tom, zda všechno proběhlo v pořádku
     */
    public async decrypt(cancellationToken: CancellationToken = CancellationToken.none): Promise<boolean> {
        if (!this.isClientSideEncrypted)
            return true;

        const pwd = await this.getEncryptionKey(cancellationToken);
        const tasks: (() => Promise<void>)[] = [];
        let decryptionSuccess = false; //true pokud se povedlo dešifrovat alespoň 1 neprázdný atribut.
        const changedAttrs: string[] = [];

        for (const [attrName, val] of this._values) {
            if (val instanceof IEncAttrValue) {
                let v = val; // musí tu být, jinak si s tím prostě kompilátor neporadí a ukazuje špatně odvozené typy po this.getValue
                if (v.isEncrypted) {
                    if (v instanceof DataOnRequest) { // kdyby náhodou nebyla hodnota lazy atributu ještě nikde zobrazená, tak se natáhne pro dešifrování
                        v = await this.getValue(attrName, cancellationToken) as IEncAttrValue;
                    }

                    //Je potřeba nejprve dešifrovat nějaký neprázdný atribut.
                    //Teprve až se podaří ověřit heslo vůči neprázdnému atributu, tak mohou být prázdné atributy nastaveny jako dešifrovány.
                    if (v.hasEncryptedValue) {
                        if (!(await v.decrypt(pwd))) {
                            this._encryptionKey = null;
                            tasks.splice(0);
                            decryptionSuccess = false;
                            break; //pokud heslo nesedí, není důvod zpracovávat další atributy
                        } else {
                            const attr = this._ciAttrName.get(attrName);
                            changedAttrs.push(attr);
                            this.attributeChanged.trigger(this, attr);
                            decryptionSuccess = true;
                        }
                    } else {
                        //prázdné atributy budou dešifrovány a odemčeny až později, podle toho, jestli se povede dešifrovat nějaký neprázdný atribut.
                        tasks.push(async () => {
                            if (await v.decrypt(pwd)) {
                                const attr = this._ciAttrName.get(attrName);
                                changedAttrs.push(attr);
                                this.attributeChanged.trigger(this, attr);
                            }
                        });
                    }
                }
            }
        }

        if (decryptionSuccess) {
            //provede se dešifrování zašifrovaných atributů, které jsou prázdné
            await Promise.all(tasks.map(t => t()));
            this.objectDecrypted.trigger(this, undefined);
        } else if (tasks.length > 0)
            this._encryptionLogger.warn("Unsupported encryption scenario. All attributes are empty. This could possibly lead to unpredictable behaviour.");

        this.attributesChanged.trigger(this, changedAttrs);
        this._isClientSideEncrypted = !decryptionSuccess;
        return decryptionSuccess;
    }

    /**
     * Vrací název třídy objektu v předané vazbě. Pokud předaný atribut není vazební, vrací null.
     * @param attributeName Jméno vazebního atributu, pro který se hledá název třídy
     * @param requestContext Viz {@link RequestContext}
     */
    public getObjectAttributeClass(attributeName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<string | null> {
        const pos = attributeName.indexOf(".");
        if (pos > -1) {
            const attrName = attributeName.substring(0, pos);
            const value = this._values.get(attrName);
            if (value instanceof ObjAttrData) {
                const attrName2 = attributeName.substring(pos + 1);
                return this._getObject(value, attrName, this, cancellationToken).then((obj1) => obj1.getObjectAttributeClass(attrName2, cancellationToken));
            }
            return null;
        }

        const val = this._values.get(attributeName);

        if (!(val instanceof ObjAttrData))
            return Promise.resolve(null);

        return this._getObject(val, attributeName, undefined, cancellationToken).then(() => val.className); // className jinak ještě nemusí být zinicializované, viz _loadAttrData()
    }

    public async getAttributeMetadata(attributePath: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<AttributeDescription | null> {
        const separatorPosition = attributePath.indexOf(".");

        // jde o vazební atribut
        if (separatorPosition > -1) {
            const attributeName = ObjectUtils.removeTypeCasting(attributePath.substring(0, separatorPosition));

            // primárně chceme najít konkrétního potomka, který může být ve vazbě - jdeme přes jednotlivé objekty uložené ve vazbě
            // ale vazba může být prázdná (nebo ji nemáme právo číst)
            const attributeValue = this._values.get(attributeName);
            const remainingAttributePath = attributePath.substring(separatorPosition + 1);

            if (attributeValue instanceof ObjAttrData) {
                const nextObjectOnPath = await this._getObject(attributeValue, attributeName, this, cancellationToken);
                if (nextObjectOnPath == null || nextObjectOnPath instanceof NullOWObject)
                    return null;

                return await nextObjectOnPath.getAttributeMetadata(remainingAttributePath, cancellationToken);
            } else {
                const staticAttributeMetadata = await this.classInfo?.getAttributeInfo(attributePath);
                if (!staticAttributeMetadata) {
                    //throw new EOWException(`Attribute path "${attributePath}" on "${this.objectName}" not found!`); //`
                    this._owObjectLogger.warn(`Attribute path "${attributePath}" on "${this.objectName}" not found!`);
                    return null;
                }

                return staticAttributeMetadata;
            }
        }

        // jde o obyčejný nevazební atribut
        return await this.classInfo?.getAttributeInfo(attributePath) ?? null;
    }

    /**
     * Načte data do objektu
     * @param data data objektu
     * @params dontReloadEncryptedAttrValues Příznak, že se nemají obnovovat zašifrované atributy
     */
    private _loadData(data: IOWObjectForTransfer, cancellationToken: CancellationToken = CancellationToken.none, triggerAttrChanged: boolean = true, dontReloadEncryptedAttrValues: boolean = false, changedAttribute: string = undefined, fullPathChangedAttrs: Array<string> = [], originalValue: any = undefined, previousAttributePath: string = undefined): Promise<OWObject> {
        cancellationToken.throwIfCancellationRequested();
        this._clearRightsCaches();

        return new Promise(async (resolve, reject) => {
            this._objectIdentity = data.ObjectIdentity;

            // ReSharper disable DoubleNegationOfBoolean
            this._isServerSideEncrypted = !!data.isEncrypted;
            this._isEncryptionFinalized = !!data.isEncryptionFinalized;

            this._canEdit = !!data.CanEdit;
            this._canDelete = !!data.CanDelete;
            // ReSharper restore DoubleNegationOfBoolean

            const encKey = this.isServerSideEncrypted && !this.isEncryptionFinalized ? await this.getEncryptionKey() : null;
            this._isClientSideEncrypted = this.isEncryptionFinalized || (!this.isNew && this.isServerSideEncrypted && encKey == null);

            this._roleIds = data.RoleIDs;
            this._editRoleIds = data.EditIDs;
            this._deleteRoleIds = data.DeleteIDs;

            this._createAttrChangeCycle = data.CreateAttrChangeCycle;
            this._attrChangeCycle = data.AttrChangeCycle;

            this.isChanged = this._attrChangeCycle > this._createAttrChangeCycle;
            this._isCopy = data.isCopy ?? false;
            this._isVersioned = data.isVersioned ?? false;
            this._workflowId = data.WorkflowID;

            const promises = new Array<Promise<OWObject>>();
            const changedAttrs = new Array<string>();
            const isRootObject = fullPathChangedAttrs.length === 0; // Prázdné je to jenom v případě prvního volání zvenku, pro kořenový atribut.

            for (const attr of data.AttrData) {
                this._loadAttrData(attr, promises, changedAttrs, fullPathChangedAttrs, triggerAttrChanged, cancellationToken, dontReloadEncryptedAttrValues, encKey, changedAttribute, originalValue, previousAttributePath);
            }

            if (promises.length) {
                Promise.all(promises).then(() => {
                    resolve(this);

                    if (isRootObject) {
                        const mergedChangedAttrs = changedAttrs.concat(fullPathChangedAttrs);

                        if (mergedChangedAttrs.length > 0) {
                            this.attributesChanged.trigger(this, mergedChangedAttrs);
                        }
                    } else if (changedAttrs.length > 0) {
                        this.attributesChanged.trigger(this, changedAttrs);
                    }
                }).catch(e => reject(e));
            } else {
                resolve(this);
            }
        });
    }

    /**
     * Načte pouze specifické vyjmenované atributy a odstraní je z předané kolekce AttrData
     * @param objectData Data k načtení
     * @param cancellationToken Token umožňující zrušení promisu
     * @param attributeNames Názvy atributů, které se mají načíst
     */
    private async _loadSpecificAttributes(objectData: IOWObjectForTransfer, cancellationToken: CancellationToken, ...attributeNames: string[]): Promise<void> {
        const names = attributeNames.filter((attr) => attr).map((attr) => attr.toUpperCase());
        const promises: Promise<OWObject>[] = [];
        const changedAttrs: Array<string> = [];
        objectData.AttrData.filter((attrData) => names.find((attrName) => attrName === attrData.Name.toUpperCase()) != null).forEach((attrData) => {
            this._loadAttrData(attrData, promises, changedAttrs, [], false, cancellationToken);
            objectData.AttrData.splice(objectData.AttrData.indexOf(attrData), 1);
        });

        await Promise.all(promises);

        if (changedAttrs.length > 0) {
            this.attributesChanged.trigger(this, changedAttrs);
        }
    }

    /**
     * Provede úvodní konfiguraci nového záznamu z vazby
     * @param obj Inicializovaný objekt
     * @param masterAttribute Atribut, ve kterém leží na master objektu
     */
    private _initNewChildObject(obj: OWObject, masterAttribute: string): void {
        obj._parentObject = this;
        obj._attributeOnParentObject = masterAttribute;
    }

    private _loadAttrData(attrData: IAttrDataForTransfer, promises: Promise<OWObject>[], changedAttrs: string[], fullPathChangedAttrs: Array<string>, triggerAttrChanged: boolean, cancellationToken: CancellationToken, dontReloadEncryptedAttrValues: boolean = false, encKey: CryptoKey | null = null, changedAttribute: string = undefined, originalValue: any = undefined, previousAttributePath: string = undefined): Promise<void> {
        const cts = new CancellationTokenSource([cancellationToken]);
        try {
            let value: IAttrValue;
            const aName: string = attrData.Name;
            let forceAttrChange = false;
            const prom = this.classInfo.getAttributeInfo(aName).then(async (aInfo) => {
                let result: any = null;
                let canFireEvent = true;
                let addToFullPathChangedAttrs = true;

                if (aInfo.basicType === BasicDataType.OWObject) {
                    if (this._disposed || cancellationToken.cancellationRequested)
                        return Promise.resolve();

                    let oad = new ObjAttrDataInternal();
                    const newAttributePath = previousAttributePath ? (previousAttributePath + "." + aName) : aName;

                    if (attrData.FullObject || (attrData.InstanceID && InstanceManager.getInstance(attrData.InstanceID))) {
                        if (attrData.FullObject) {
                            const rawObj = attrData.Value as IOWObjectForTransfer;
                            oad.className = rawObj.ClassName;
                            oad.instanceID = rawObj.InstanceID;
                            oad.id = rawObj.AttrData.find(attr => attr.Name === "ID").Value?.toString();
                        } else {
                            oad.className = attrData.ClassName;
                            oad.instanceID = attrData.InstanceID;
                            oad.id = attrData.Value?.toString();
                        }

                        const existingInstance = InstanceManager.getInstance(oad.instanceID);
                        if (existingInstance) {
                            this._owObjectLogger.debug(`Reusing instance: ${existingInstance.instanceID} of class '${existingInstance.objectName}' and id ${existingInstance.id} in association '${aName}' of instance ${this.instanceID} of class '${this.objectName}' and id ${this.id}`);
                            InstanceManager.addInstanceUsage(existingInstance); // zvýší počet referencí nezbytný pro správné uvolňování instancí

                            oad.object = (async () => {
                                let obj: OWObject;
                                // pokud přijatá data obsahují novější změny, tak se jimi nahradí stávající hodnoty atributů
                                if ((attrData.Value as IOWObjectForTransfer).AttrChangeCycle > existingInstance._attrChangeCycle) {
                                    addToFullPathChangedAttrs = false;
                                    obj = await existingInstance._loadData(attrData.Value, cancellationToken, true, false, undefined, fullPathChangedAttrs, undefined, newAttributePath);
                                } else {
                                    obj = existingInstance;
                                }
                                this._initNewChildObject(obj, aName);
                                return obj;
                            })();
                        } else {
                            oad.object = OWObject.createObject(attrData.Value, false, this._context, (obj) => this._initNewChildObject(obj, aName), cancellationToken);
                        }

                        oad.object = oad.object.then(obj => {
                            if (this._disposed || cancellationToken.cancellationRequested) {
                                obj.dispose();
                                oad.object = undefined;
                            } else {
                                oad.loaded = true;

                                if (obj._parentObject === undefined) {
                                    obj._parentObject = this;
                                    obj._attributeOnParentObject = aName;
                                }

                                oad.loadedInstance = obj;
                            }
                            return obj;
                        }).catch(err => {
                            oad.object = undefined;
                            throw err;
                        });

                        if (this._values.has(aName)) {
                            const previousValue = this._values.get(aName) as ObjAttrDataInternal;
                            // pokud se mění instance ve vazbě na jinou, tak je nutné tu původní uvolnit
                            if (previousValue.instanceID !== oad.instanceID)
                                this._disposeInstanceAttrData(previousValue);
                        }

                        result = value = oad;
                    } else if (attrData.OnlyChanges) {
                        oad = value = this._values.get(aName) as ObjAttrDataInternal;
                        if (oad.object) { // oad.object může být undefined (viz následující else blok)
                            addToFullPathChangedAttrs = false;
                            result = oad.object.then((obj) => obj._loadData(attrData.Value, cancellationToken, true, false, undefined, fullPathChangedAttrs, undefined, newAttributePath));
                            canFireEvent = !!(attrData.Value as IOWObjectForTransfer).AttrData.length; // Vyvolávat událost o změně jen pokud se změnily nějaké atributy.
                        } else if (attrData.ClassName !== undefined) { // pokud není className, tak se jedná o nedočtený objekt (pravděpodobně kompozit)
                            oad.className = attrData.ClassName;
                            oad.id = attrData.Value?.toString();
                        }
                    } else {
                        oad = new ObjAttrDataInternal();
                        oad.className = attrData.ClassName;
                        oad.id = attrData.Value?.toString();
                        value = oad;

                        if (this._values.has(aName)) {
                            const previousValue = this._values.get(aName) as ObjAttrDataInternal;
                            this._disposeInstanceAttrData(previousValue);
                        }
                    }
                } else {
                    if (attrData.DataOnRequest) {
                        const bda = new DataOnRequest(attrData.IsEncrypted);
                        value = bda;
                    } else if (attrData.IsEncrypted) {
                        if (!dontReloadEncryptedAttrValues || (!aInfo.supportsEncValue && this.isClientSideEncrypted)) {
                            const deser = await this.getSerializers(aInfo);
                            const deserializer = (aInfo.basicType === BasicDataType.Document)
                                ? (v: any) => {
                                    const { fullAttrName, masterObject } = OWObject._getFullMasterObjAttrName(this, aName);
                                    return SerializeFuncs.deserializeOWDocument(v, masterObject.instanceID, fullAttrName);
                                }
                                : (deser && deser.deserializer ? deser.deserializer : undefined);

                            value = new EncAttrValue(attrData.Value, !aInfo.supportsEncValue, aInfo.defaultValue, deserializer);

                            // pokud už existuje vhodný dešifrovací klíč, tak se hodnota rovnou dešifruje
                            if (encKey)
                                await (value as IEncAttrValue).decrypt(encKey);
                        } else { // pokud nechci obnovovat hodnotu zašifrovaného atributu
                            // Zašifrovanou hodnotu vrácenou ze serveru nebudu nastavovat, bylo by zbytečné ji hned zase rozšifrovávat.
                            // Nastavím pouze v případě, že přišla prázdná hodnota - např. byla vymazána apl. logikou.
                            if (attrData.Value === "") {
                                value = ""; // nastavím prázdnou, pokud byla úplně odstraněna
                            } else if (!aInfo.supportsEncValue) { // pokud atribut nepodporuje šifrované hodnoty, tak přichází rovnou otevřená hodnota
                                value = attrData.Value;
                            } else {
                                if (!this._tempDecryptedValues.has(aName)) {
                                    this._owObjectLogger.warn(`Missing decrypted temporary "${aName}" attribute value. Possibly caused by loading same decrypted attribute multiple times.`); // `
                                    return result;
                                }
                                value = this._tempDecryptedValues.get(aName); // jinak ponechám původní hodnotu
                                this._tempDecryptedValues.delete(aName);
                            }
                        }
                    } else if (attrData.Value && (aInfo.basicType === BasicDataType.Document)) {
                        const { fullAttrName, masterObject } = OWObject._getFullMasterObjAttrName(this, aName);
                        value = SerializeFuncs.deserializeOWDocument(attrData.Value, masterObject.instanceID, fullAttrName);
                    } else {
                        const deser = await this.getSerializers(aInfo);
                        value = deser && deser.deserializer ? deser.deserializer(attrData.Value) : attrData.Value;

                        if (changedAttribute && originalValue && changedAttribute == aName) {
                            // TODO: porovnávat deserializované hodnoty, momentálně takto kvůli porovnávání localizedTextů
                            forceAttrChange = originalValue != attrData.Value;
                        }

                        if (aName.toUpperCase() === "ID") {
                            // tyto vlastnosti je vhodné mít načtené už před vyvoláváním AttributeChanged
                            this._id = value?.toString() ?? null;
                        }
                    }
                }

                // pokud jiz prvek existuje a je shodny zakazeme volani udalosti, protoze je zbytecna
                // pokud jde o agregovany objekt (composite nebo shared) je potreba udalost vyvolat
                if (triggerAttrChanged && this._values.has(aName) && OWObject._attributeValueComparer(this._values.get(aName), value) && !(aInfo.basicType === BasicDataType.OWObject && (aInfo as IOWClassAttributeDescription).aggregation !== Aggregation.none)) {
                    canFireEvent = false;
                } else {
                    this._values.set(aName, value);
                    this._ciAttrName.set(aName, aName);
                }

                this._updatingAttrs.splice(this._updatingAttrs.findIndex(rau => rau.task === prom));
                if ((triggerAttrChanged && canFireEvent) || forceAttrChange) {
                    if (addToFullPathChangedAttrs && previousAttributePath) {
                        fullPathChangedAttrs.push(previousAttributePath + "." + aName);
                    }

                    changedAttrs.push(aName);
                    this.attributeChanged.trigger(this, aName);
                    if (this.relationAttributeChanged.containsAnyListener()) {
                        this._triggerRelationAttributeChangedOnTree(attrData);
                    }
                }
                return result;
            }).catch((e: Error) => {
                this._updatingAttrs.splice(this._updatingAttrs.findIndex(rau => rau.task === prom));
                throw e;
            });
            promises.push(prom);
            this._updatingAttrs.push({ task: prom, cts: cts });
            return prom;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /** Vyvolá relationAttributeChanged rekurzivně na všech změněných atributech. V jejich názvech zachovává informace o všech vazbách. */
    private _triggerRelationAttributeChangedOnTree(data: IAttrDataForTransfer, name: string = ""): void {
        const resultAttrName = (name === "") ? data.Name : `${name}.${data.Name}`;
        if (data.Type === "OWObject" && data.Value?.AttrData) {
            data.Value.AttrData?.forEach ((attr: IAttrDataForTransfer) => this._triggerRelationAttributeChangedOnTree(attr, resultAttrName));
        } else {
            this.relationAttributeChanged.trigger(this, resultAttrName);
        }
    }

    private _disposeInstanceAttrData(instanceData: ObjAttrDataInternal): void {
        if (instanceData.loadedInstance != null)
            instanceData.loadedInstance.dispose();
        else if (instanceData.object != null)
            instanceData.object.then(o => o.dispose());
    }

    /**
     * Porovna hodnoty dvou atributu
     * @param val1 Atribut 1
     * @param val2 Atribut 2
     */
    private static _attributeValueComparer(val1: IAttrValue, val2: IAttrValue): boolean {
        if (val1 == null && val2 == null) return true;
        if (val1 == null || val2 == null) return false;

        if (val1 instanceof IComparable && val2 instanceof IComparable) return val1.compare(val2);

        const v1of = val1.valueOf();
        const v2of = val2.valueOf();

        if (typeof v1of !== typeof v2of) return false;
        if (v1of instanceof NullOWObject && v2of instanceof NullOWObject) return true;
        if (v2of instanceof Date && v1of instanceof Date) return v2of.toString() === v1of.toString();
        if (v1of instanceof IComparable && v2of instanceof IComparable) return v1of.compare(v2of);

        return v1of === v2of;
    }

    public compare(value: any): boolean {
        if (value instanceof IOWObject) {
            return this._attrChangeCycle == (value as OWObject)._attrChangeCycle && this.instanceID == value.instanceID;
        }
        return false;
    }

    /**
     * Nalezne první nadřízený záznam, který je vypublikovaný a vrátí ho spolu s cestou z něj na předanou instanci
     * @param object Instance, ke které se hledá vypublikovaná cesta
     * @param attribute Volitelný atribut na předané instanci, který se přidá na konec nalezené cesty
     */
    private static _getFullMasterObjAttrName(object: OWObject, attribute?: string): { fullAttrName: string; masterObject: OWObject } {
        let fullAttrName = attribute;
        let masterObject = object;
        let i = 0; // kontrolni zarazka

        while (!masterObject._isPublicObject && masterObject._parentObject != null && i++ < 50) {
            const attr = masterObject._attributeOnParentObject.split(".");
            const appendAttrName = (attr.length > 1 ? attr[attr.length - 1] : masterObject._attributeOnParentObject);
            //const masterId = `${masterObject.instanceID}|${appendAttrName}`;//`

            //fullAttrName = (attr.length > 1 ? attr[1] : masterObject.attributeOnParentObject) + "." + fullAttrName;
            fullAttrName = fullAttrName ? `${appendAttrName}.${fullAttrName}` : appendAttrName;//`

            masterObject = masterObject._parentObject;
        }

        return { fullAttrName: fullAttrName, masterObject: masterObject };
    }

    /**
     * Provede serverovou validaci objektu. Pokud jsou nalezeny nějaké validační chyby, tak jsou vyhozeny jako validační výjimka.
     */
    public validate(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        return OWManager.validateObject(this.instanceID, this._getRequestContext(cancellationToken));
    }

    /**
     * Provede serverovou odlozenou validaci objektu. Pokud jsou nalezeny nějaké validační chyby, tak jsou vyhozeny jako validační výjimka.
     */
    public postponedValidate(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        return OWManager.postponedValidateObject(this.instanceID, this._getRequestContext(cancellationToken));
    }

    /**
     * Interní metoda pro vrácení hodnoty atributu. Je možné vypnout čekání na update hodnot - používat opatrně.
     * @param attributePath Název atributu, může obsahovat i cestu přes vazební atributy (přes tečky)
     * @param skipWaitForUpdateInstance Pokud je true, nečeká se na doběhnutí aktualizace hodnot
     */
    private async _getValue(attributePath: string, skipWaitForUpdateInstance = false, cancellationToken: CancellationToken = CancellationToken.none): Promise<IAttrValue> {
        if (!attributePath)
            throw new InvalidArgumentException("Trying to load unspecified attribute");

        // pokud prave probiha nejaky update hodnot, tak je potreba pockat na jeho dobehnuti, abychom cetli z instance v konzistentnim stavu
        if (skipWaitForUpdateInstance === false)
            await this.waitForUpdateInstance(cancellationToken);

        const separatorPosition = attributePath.indexOf(".");
        const attributeName = separatorPosition > -1 ? attributePath.substring(0, separatorPosition) : attributePath;

        const [attributeNameWithoutTypeCast, typecastingClass] = StringUtils.splitFirst(attributeName, "|");
        let attributeValue = this._values.get(attributeNameWithoutTypeCast);

        if (attributeValue === undefined) {
            if (attributeNameWithoutTypeCast === "IsNew")
                attributeValue = this.isNew;
            else if (attributeNameWithoutTypeCast === "IsNewCopy")
                attributeValue = this.isNew && this.isCopy;
            else if (attributeNameWithoutTypeCast === "IsVersioned")
                attributeValue = this.isVersioned;
            else {
                // na klientovi není hodnota takového atributu dostupná, tak zkontrolujeme, jestli vůbec atribut existuje v definici třídy
                // pokud by atribut vůbec neexistoval, tak vznikne výjimka
                const attrDesc = await this.classInfo.getAttributeInfo(attributeNameWithoutTypeCast);
                if (!attrDesc)
                    this._owObjectLogger.warn(`Attribute "${attributeNameWithoutTypeCast}" was not found on ${this.objectName} - can't find attribute ${attributePath}!`);
                // jinak víme, že existuje, ale neznáme jeho hodnotu
                return undefined;
            }
        }

        if (attributeValue instanceof ObjAttrData && !StringUtils.isNullOrEmpty(typecastingClass) && attributeValue.className != typecastingClass) {
            this._owObjectLogger.warn(`Invalid typecasting on attribute '${attributeNameWithoutTypeCast}'. Got ${attributeValue.className}, expected ${typecastingClass}.`);
            //throw new InvalidTypeCasting(attributeNameWithoutTypeCast, typecastingClass, attributeValue.className);
        }

        // jde o vazební atribut
        if (separatorPosition > -1) {
            if (attributeValue instanceof ObjAttrData) {
                const remainingAttributePath = attributePath.substring(separatorPosition + 1);
                const nextObjectOnPath = await this._getObject(attributeValue, attributeNameWithoutTypeCast, this, cancellationToken);

                return await nextObjectOnPath.getValue(remainingAttributePath, cancellationToken); // rekurze
            } else
                throw new EOWException(`Attribute "${attributeNameWithoutTypeCast}" on ${this.objectName} is not an object attribute - can't find attribute ${attributePath}!`); //`
        } else {
            if (attributeValue instanceof ObjAttrData) {
                return await this._getObject(attributeValue, attributeNameWithoutTypeCast, this, cancellationToken);
            } else if (attributeValue instanceof DataOnRequest)
                return await this._getDataOnRequest(attributeNameWithoutTypeCast, cancellationToken);
            else
                return attributeValue;
        }
    }

    public getValue(attributeName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<IAttrValue | undefined> {
        return this._getValue(attributeName, undefined, cancellationToken);
    }

    /**
     * Vrací hodnotu atributu pro vyhodnocování podmínek - object s ID = 0 je vrácen jako null
     * !!! Tuto metodu používají podmínky práv generované serverem !!!
     * @param attributeName Název atributu
     */
    public async getValueForCondition(attributeName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        const attrValue = await this.getValue(attributeName, cancellationToken);
        const resultValue = attrValue ? attrValue.valueOf() : attrValue;

        if (resultValue instanceof NullOWObject)
            return null; //asi nejakou chybou NullOWObject nema nastaveno ID na 0

        if (resultValue instanceof OWObject) {
            if (resultValue.id === "0" && !resultValue.isNew)
                return null;
            else
                return resultValue.id;
        }

        if (resultValue instanceof Enum)
            return Number(resultValue);

        return resultValue;
    }

    // pomocna promena pro to aby se zabranilo volani metody GetAttributesValues vickrat pro jeden atribut
    private _loadingAttributeInProcess: Map<string, Promise<IAttrValue>> = new Map();

    private async _getDataOnRequest(attributeName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<IAttrValue> {
        let loadProcess = this._loadingAttributeInProcess.get(attributeName);
        // neco jak zamek, bez zdrzovani - bezne nastava ze se klient pokousi z nekolika mist cist ten samy atribut, tak aby se GetAttributesValues nevolalo vickrat
        if (!loadProcess) {
            loadProcess = (async (attrName) => {
                const data = await OWManager.getAttributesValues(this.instanceID, [attrName], false, this._getRequestContext((cancellationToken)));
                await this._loadData(data, cancellationToken, false); // při čtení hodnoty by se _loadData() nemělo chovat standardně a nemělo by vyvolávat událost změny atributu, protože šlo jen o čtení a ne o změnu
                return await this.getValue(attrName, cancellationToken);
            })(attributeName);
            this._loadingAttributeInProcess.set(attributeName, loadProcess);
            loadProcess.then(() => this._loadingAttributeInProcess.delete(attributeName));
        }
        /*
        else
            this._owObjectLogger.log(`attribute ${attributeName} is loading.`);
        */
        return loadProcess;
    }

    public getValues(attrNames: string[], cancellationToken: CancellationToken = CancellationToken.none): Promise<IAttrValue[]> {
        return new Promise<any[]>(resolve => {
            const arr: IAttrValue[] = [];
            const prm: any[] = [];
            let i = 0;
            const fn = (promise: Promise<IAttrValue>, index: number): Promise<IAttrValue> => {
                return promise.then((v) => {
                    arr[index] = v;
                    return v;
                });
            };
            for (const attrName of attrNames) {
                prm.push(fn(this.getValue(attrName, cancellationToken), i++));
            }

            Promise.all(prm).then(() => {
                resolve(arr);
            });
        });
    }

    public getParameters(attributes: readonly string[], cancellationToken: CancellationToken = CancellationToken.none): Promise<ConditionParams> {
        if (attributes == null)
            return null;

        const promises = new Array<Promise<ConditionParam>>();
        for (const attr of attributes) {
            const prom = this.getValue(attr, cancellationToken).then((value) => {
                value = AttrValueReader.read(value);

                if (value instanceof Date) {
                    return DateUtils.toXsdDateTime(value);
                }
                if (value instanceof Enum) {
                    return Number(value);
                }
                if (value instanceof NullOWObject)
                    return null;

                if (value instanceof IOWObject)
                    return value.id;

                return value;
            });
            promises.push(prom);
        }

        return Promise.all(promises);
    }

    public waitForUpdateInstance(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        cancellationToken.register(() => this.cancelUpdatesInProgress());
        return Promise.all(this._updatingAttrs.map(rau => rau.task)).then(_ => undefined);
    }

    public async setValues(attributes: IChangedAttr[], cancellationToken: CancellationToken = CancellationToken.none): Promise<OWObject> {
        const cts = new CancellationTokenSource([cancellationToken]);
        const patchData = new Array<any>();
        let changingObject: OWObject = undefined;
        let attributeName: string;
        let resultObj;

        for (const attribute of attributes) {
            attributeName = attribute.attributeName;
            const ai = await this.getAttributeMetadata(attribute.attributeName, cts.token);

            if (!this._parentObject) {
                const fn = OWObject._getFullMasterObjAttrName(this, attribute.attributeName);
                changingObject = fn.masterObject;
                attributeName = fn.fullAttrName;
            }

            patchData.push({
                value: await this._serialize(ai, attributeName, attribute.value),
                attributeName: attribute.attributeName,
            });
        }

        try {
            const setResult = await this._patchObject(changingObject || this, patchData.map(x => x.value), undefined, cts.token);
            for (const data of patchData) {
                resultObj = await this._loadData(setResult, cts.token, undefined, true, data.attributeName, [], data.value);
            }
        } catch (error) {
            ErrorHandler.instance.handleError(error);
        }

        return resultObj;
    }

    private async _serialize(ai: AttributeDescription, attributeName: string, value: any): Promise<IOWObjectJsonPatchDTO> {
        if (ai == null)
            return Promise.reject(new EOWException(`Class ${this.objectName} doesn't contain attribute ${attributeName}!`)); //`

        let serializedValue: any;
        let isValueEncrypted: boolean = false;
        if (value == null) {
            serializedValue = null;
        } else {
            const serializer = await this.getSerializers(ai);

            //jeste se to bude resit jak se bude hodnota posilat, zatim musi byt vse v JSON/XML formatu v retezci
            serializedValue = serializer && serializer.serializer ? serializer.serializer(value, this._values.get(attributeName)) : value; // serializace
            serializedValue = typeof serializedValue === "string" ? serializedValue : JSON.stringify(serializedValue);

            if (this.isServerSideEncrypted && ai.supportsEncValue && ai.encryptingAllowed) {
                if (this.isEncryptionFinalized)
                    throw new InvalidOperationException("OWObject: Cannot set attribute value on finalized encrypted object.");

                this._tempDecryptedValues.set(attributeName, value); // v odpovědi vrací server zašifrovanou hodnotu, takže si ji zde uchovám pro nastavení po přijetí serverem

                const encryptionKey = await this.getEncryptionKey(); // vlastní osoleny cryptokey
                const encryptedValue = await SecurityUtils.encryptString(serializedValue, encryptionKey); // zašifrování
                serializedValue = DataConverter.bufferToBase64(encryptedValue); // zašifrované pole na b64
                isValueEncrypted = true;
            }
        }

        if (serializedValue != null)
            serializedValue = typeof serializedValue === "string" ? serializedValue : JSON.stringify(serializedValue);

        return { op: (isValueEncrypted ? "setEncryptedValue" : "setValue"), name: attributeName, value: serializedValue };
    }

    public async setValue(attributeName: string, value: any, cancellationToken: CancellationToken = CancellationToken.none): Promise<OWObject> {
        const cts = new CancellationTokenSource([cancellationToken]);
        try {
            const promise = this.setValues([{ attributeName: attributeName, value: value }], cancellationToken).then(async (resultObj): Promise<OWObject> => {
                this._updatingAttrs.splice(this._updatingAttrs.findIndex(rau => rau.task === promise));
                this.valueSet.trigger(this,
                    {
                        attributeName,
                        value: this._values.get(attributeName),
                    });
                return resultObj;
            }).catch((e: Error) => {
                this._updatingAttrs.splice(this._updatingAttrs.findIndex(rau => rau.task === promise));
                throw e;
            });
            this._updatingAttrs.push({ task: promise, cts: cts });
            return promise;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    private _addItemToHistory(patchData: IOWObjectJsonPatchDTO): void {
        if (this._changeHistory.length - 1 > this._historyPosition)
            this._changeHistory = this._changeHistory.slice(0, this._historyPosition + 1);
        this._changeHistory.push(patchData);
        this._historyPosition++;
    }

    private _patchObject(obj: OWObject, patchDatas: IOWObjectJsonPatchDTO[], withoutBigData: boolean = true, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        patchDatas.forEach(patchData => {
            obj._addItemToHistory(patchData);
        });
        return OWManager.patchObject(obj.instanceID, patchDatas, withoutBigData, this._getRequestContext(cancellationToken));
    }

    public async undo(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        if (this._historyPosition < 0)
            return;

        this._historyPosition--;
        await this.reload(cancellationToken); // vyřešit u nového objektu
        if (this._historyPosition > -1) {
            const setResult = await OWManager.patchObject(this.instanceID, this._changeHistory.slice(0, this._historyPosition + 1), undefined, this._getRequestContext(cancellationToken));
            /*const resultObj = */await this._loadData(setResult, cancellationToken, undefined, true);
        }
    }

    public async redo(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        if (this._changeHistory.length - 1 === this._historyPosition)
            return;

        this._historyPosition++;
        const setResult = await OWManager.patchObject(this.instanceID, [this._changeHistory[this._historyPosition]], undefined, this._getRequestContext(cancellationToken));
        /*const resultObj = */await this._loadData(setResult, cancellationToken, undefined, true);
    }

    /**
     * Načte vazbu - pokud ji už má nebo se načítá, tak vrací ji, jinak novou
     * @param {dataType} popis neznameho atributu mel by byt u parent
     * @param value popis value
     * @param attribute popis attribute
     */
    private async _getObject(value: ObjAttrData, attribute: string, parent?: OWObject, cancellationToken: CancellationToken = CancellationToken.none): Promise<OWObject> {
        if (this._disposed)
            return new NullOWObject();

        const fn = OWObject._getFullMasterObjAttrName(parent || this, attribute);

        const releaser = await this._getAssociationLoadingLock(fn.fullAttrName).lock();
        try {
            if (value.object) {
                try {
                    return await value.object;
                } catch (e) {
                    PromiseUtils.cancellationCatch(e); // pokud je načítání vazby cancelled, tak se pokračuje novým standardním načtení
                }
            }

            return await this._loadAssociation(value as ObjAttrData & ILoadedAssociationInternal, fn.masterObject.instanceID, fn.fullAttrName, parent, cancellationToken);
        } finally {
            releaser.release();
        }
    }

    private _getAssociationLoadingLock(attributeName: string): AsyncLock {
        if (this._associationLoadingLocks.has(attributeName))
            return this._associationLoadingLocks.get(attributeName);

        const lock = new AsyncLock();
        this._associationLoadingLocks.set(attributeName, lock);
        return lock;
    }

    /**
     * Spustí načtení vazby ze služby
     */
    private async _loadAssociation(value: ObjAttrData & ILoadedAssociationInternal, parentInstanceId: string, attributeName: string, parent?: OWObject, cancellationToken: CancellationToken = CancellationToken.none): Promise<OWObject> {
        if (!HasPositiveID(value.id))
            return new NullOWObject();

        const initFunc = (obj: OWObject) => {
            if (obj._parentObject === undefined && parent) {
                obj._parentObject = parent;
                obj._attributeOnParentObject = attributeName;
            }

            value.instanceID = obj.instanceID;
            value.loadedInstance = obj;
        };

        value.object = OWManager.getAttributeObject(parentInstanceId, attributeName, initFunc, this._getRequestContext(cancellationToken)).catch(err => {
            value.object = undefined;
            throw err;
        });

        const obj = await value.object;

        value.loaded = true;
        if (this._disposed) //pokud byl rodič objektu už odstraněn, odstraní i nově vytvořený. Předejde tak tomu, aby se zbytečně držela instance, která už nikdy nebude použita.
            obj.dispose();

        return obj;
    }

    public async getLinkNamesForInstance(instanceID: string): Promise<string[]> {
        const foundKeys: string[] = [];
        const visitedObjects: string[] = [];
        await this._getLinkNamesForInstance(instanceID, this, undefined, foundKeys, visitedObjects);
        return foundKeys;
    }

    private async _getLinkNamesForInstance(instanceID: string, obj: OWObject, keyPrefix: string, foundLinks: string[], visitedObjects: string[], recursive: boolean = true): Promise<void> {
        //musí se ale pohlídat aby si uměl poradit s cyklickou vazbou a nekontroloval které objekty už procházel.
        if (!instanceID || visitedObjects.indexOf(obj.instanceID) > -1)
            return;
        visitedObjects.push(obj.instanceID);

        for (const [k, val] of obj._values.entries()) {
            if (val instanceof ObjAttrData) {
                const key = keyPrefix ? `${keyPrefix}.${k}` : k; //`
                if (val.instanceID === instanceID) {
                    foundLinks.push(key);
                }
                if (recursive && val.loaded)
                    await this._getLinkNamesForInstance(instanceID, (await val.object), key, foundLinks, visitedObjects);
            }
        }
    }

    public async delete(cancellationToken: CancellationToken = CancellationToken.none): Promise<IResultMessage[]> {
        if (!this.isNew) {
            const result = await OWManager.objectDelete(this.instanceID, this._getRequestContext(cancellationToken));

            if (result.length === 0) {
                this.deleted.trigger(this, undefined);
            }

            return result;
        }

        return Promise.resolve([]);
    }

    public async applyUpdates(cancellationToken: CancellationToken = CancellationToken.none): Promise<IResultMessage[]> {
        if (!this.isChanged && !this.isNew)
            return [];
        else {
            await this.waitForUpdateInstance(cancellationToken);
            this.beforeSave.trigger(this, undefined);
            const wasNew = this.isNew;
            const data = await OWManager.objectApplyUpdates(this.instanceID, this._getRequestContext(cancellationToken));
            await this._loadData(data.actualizedObjectData, cancellationToken);
            this.isChanged = false;
            if (wasNew && !this.isNew) { // zperzistentnění objektu
                this.newSaved.trigger(this, undefined);
                InstanceManager.persistObject(this);
                if (this.isServerSideEncrypted) {
                    PasswordManager.movePasswordFromTemp(this.instanceID, this.objectName, this.id);
                }
            }

            this.saved.trigger(this, undefined);
            return data.messages;
        }
    }

    private _clearRightsCaches(): void {
        this._itemRightsCache.clear();
        this._conditionEvaluationsCache.clear();
    }

    private async _addOrUpdateRightCacheItem<T>(itemKey: string, cacheMap: Map<string, CachedRightItem<T>>, getValue: () => Promise<T>, cancellationToken: CancellationToken = CancellationToken.none): Promise<T> {
        let cachedValue = cacheMap.get(itemKey);
        if (cachedValue && cachedValue.time > OWManager.lastCallThatMayAffectRightsTime && !cachedValue.cancellationToken.cancellationRequested) {
            try {
                return await cachedValue.getRightValuePromise;
            } catch (error) {
                if (!cancellationToken.cancellationRequested) {
                    PromiseUtils.cancellationCatch(error);
                }
            }
        }
        cachedValue = {
            time: Date.now(),
            getRightValuePromise: getValue(),
            cancellationToken,
        };
        cacheMap.set(itemKey, cachedValue);
        return await cachedValue.getRightValuePromise;
    }

    public itemRights(itemName: string, type: ClassItemType, evalRights: ClassItemRightTypes, cancellationToken: CancellationToken = CancellationToken.none): Promise<IClassItemRights> {
        if (!itemName) {
            throw new EOWRightEvaluationException("", "Unable to evaluate rights for attribute. Attribute name is empty!");
        }

        return this._addOrUpdateRightCacheItem(
            itemName,
            this._itemRightsCache,
            () => OWManager.objectItemsRights(this.instanceID, itemName, type, evalRights, this._getRequestContext(cancellationToken)).then((result) => {
                if (result.ExceptionText) {
                    throw new EOWRightEvaluationException(itemName, result.ExceptionText, this.instanceID, this.objectName);
                }
                return result;
            }),
        );
    }

    public evaluateCondition(conditionID: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<boolean> {
        return this._addOrUpdateRightCacheItem(
            conditionID,
            this._conditionEvaluationsCache,
            () => OWManager.evaluateCondition(this, conditionID, this._getRequestContext(cancellationToken)),
        );
    }

    public cancelUpdatesInProgress(): void {
        this._updatingAttrs.forEach(rau => rau.cts.cancel());
        this._updatingAttrs = [];
    }

    public run(config: RunMethodBaseParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> {
        return OWManager.runObjectMethod({ ...config, instanceID: this.instanceID, requestContext: this._getRequestContext(cancellationToken) });
    }

    public runWithParams(config: RunMethodWithParamsBaseParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> {
        return OWManager.runObjectMethodWithParams({ ...config, instanceID: this.instanceID, requestContext: this._getRequestContext(cancellationToken) });
    }

    private static _workFlowSort(a: IWorkflowMenuItem, b: IWorkflowMenuItem): number {
        if (a.Order > b.Order) {
            return 1;
        }
        if (a.Order < b.Order) {
            return -1;
        }
        // a must be equal to b
        return 0;
    }

    public mapSignDataToTemplate(template: ISignatureTemplate, serializationFormat: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<object> {
        if (!template) {
            this._encryptionLogger.error("Template for signing of object is null or undefined");
            throw new InvalidArgumentException("template is null or undefined");
        }

        if (this.objectName !== template.className)
            throw new InvalidArgumentException("Object name mismatch");

        if (serializationFormat === "JSON_v4") {
            return ObjectSignatureSerializer.mapSignDataToTemplateJSON(this, template, this._getRequestContext(cancellationToken));
        }

        throw new InvalidOperationException(`Serialization format '${serializationFormat}' unsupported`); //`
    }

    public wfMenu(sort: boolean = true, cancellationToken: CancellationToken = CancellationToken.none): Promise<IWorkflowMenuItem[]> {
        if (this.isTempObject) {
            const objectList = new OWObjectList();
            this._tempObjectIDs.forEach(id => objectList.add({ className: this.objectName, id: id }));
            return OWManager.objectsWFMenu(objectList, this._getRequestContext(cancellationToken)).then((items) => {
                if (sort)
                    return items.sort(OWObject._workFlowSort);
                else
                    return items;
            });
        } else
            return OWManager.objectWFMenu(this.instanceID, this._getRequestContext(cancellationToken)).then((items) => {
                if (sort)
                    return items.sort(OWObject._workFlowSort);
                else
                    return items;
            });
    }

    public createCopy(cancellationToken: CancellationToken = CancellationToken.none): Promise<OWObject> {
        return OWManager.createCopyObject(this.instanceID, undefined, this._getRequestContext(cancellationToken));
    }

    /**
     * Kontroluje, zda má objekt přiřazené nějaké WorkFlow
     * @param requestContext Viz {@link RequestContext}
     */
    public async hasWf(cancellationToken: CancellationToken = CancellationToken.none): Promise<boolean> {
        try {
            if (this.isNew || this.isTempObject) {
                return await OWManager.isWFDefinedToClass(this.objectName, this._getRequestContext(cancellationToken));
            } else {
                return this.WorkflowID != null;
            }
        } catch (exp) {
            return false;
        }
    }

    /**
     * Vrátí ID objektu v daném atributu
     * @param attributeName Název atributu
     */
    public async getObjectAttributeID(attributeName: string, _cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        if (attributeName.includes("."))
            throw new Error("Not implemented"); // dalo by se dodělat, musely by se už načítat opravdu vazební instance až po tu poslední (zatím to nepotřebujeme)

        const val = this._values.get(attributeName);
        if (!val || !(val instanceof ObjAttrData))
            return "0";

        return val.id;
    }

    public getImplicitForm(attrName: string = "", conditionParameters: string = "", cancellationToken: CancellationToken = CancellationToken.none): Promise<IImplicitFormInfo | null> {
        return OWManager.getInstanceImplicitForm(this.instanceID, attrName, conditionParameters, this._getRequestContext(cancellationToken));
    }

    public async isAttributeReadOnly(attrName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<boolean> {
        if (!attrName)
            throw new InvalidArgumentException("attrName");

        const info = await this.getAttributeMetadata(attrName, cancellationToken);
        return info ? !!info.readOnly : true;
    }

    public async isAttributeEncrypted(attrName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<boolean> {
        if (!attrName)
            throw new InvalidArgumentException("attrName");

        if (!this.isServerSideEncrypted)
            return false;

        const info = await this.getAttributeMetadata(attrName, cancellationToken);
        return info ? !!info.encryptingAllowed : false;
    }

    public createSignature(signature: string, signedData: string, profileId: string, providerType: string, providerVersion: string,
        signatureSourcesEncrypted: boolean = false, cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        return OWManager.createObjectsSignature([{
            signatures: [{ instanceID: this.instanceID, signature: signature, signedData: signedData }],
            profileId: profileId,
            providerType: providerType,
            providerVersion: providerVersion,
        }], signatureSourcesEncrypted, this._getRequestContext(cancellationToken));
    }

    /**
     * Uvolní objekt a rekurzivně i jeho vazby
     */
    public dispose(): void {
        if (this._disposed)
            return;

        if (!InstanceManager.removeObject(this))
            return;

        this._disposed = true;

        this.cancelUpdatesInProgress();

        this.disposed.trigger(this, undefined);
        this.disposed.removeAll();

        this._owObjectLogger.debug(`Disposed instance: ${this.instanceID} id ${this.id} of class '${this.objectName}'`);

        this._disposeAssociations();
        this._removeEventListeners();
    }

    /**
     * Odstranění všech event listenerů
     */
    private _removeEventListeners(): void {
        this.valueSet.removeAll();
        this.attributeChanged.removeAll();
        this.newSaved.removeAll();
        this.beforeSave.removeAll();
        this.saved.removeAll();
        this.validityChange.removeAll();
        this.dataRefreshed.removeAll();
        this.objectDecrypted.removeAll();
        this.disposed.removeAll();
        this.deleted.removeAll();
    }

    /**
     * Uvolní vazby záznamu
     */
    private _disposeAssociations(): void {
        this.cancelUpdatesInProgress();

        const loadedAssociations = Array.from(this._values.entries())
            .filter(([, a]) => a instanceof ObjAttrData && a.loaded)
            .filter(([, a]) => (a as ILoadedAssociationInternal).loadedInstance != undefined && !((a as ILoadedAssociationInternal).loadedInstance instanceof NullOWObject))
            .map(([attrName, a]) => ({ attrName, dispAttr: a as IDisposable, obj: (a as ILoadedAssociationInternal).loadedInstance }));

        loadedAssociations.forEach(({ dispAttr }) => dispAttr.dispose());

        const disposedAssociations = loadedAssociations.filter(({ obj }) => obj._disposed);
        if (disposedAssociations.length > 0) {
            this._owObjectLogger.groupCollapsed(`Disposed ${disposedAssociations.length} associated instances of: `, this.instanceID, this.objectName, this.id);
            disposedAssociations
                .forEach(({ attrName, obj }) => this._owObjectLogger.debug(`Instance ${obj.instanceID} id ${obj.id} of class '${obj.objectName}' placed in attribute '${attrName}'`));
            this._owObjectLogger.groupEnd();
        }
        const undisposedAssociations = loadedAssociations.filter(({ obj }) => !obj._disposed);
        if (undisposedAssociations.length > 0) {
            this._owObjectLogger.groupCollapsed(`Not disposed ${undisposedAssociations.length} associated instances of: `, this.instanceID, this.objectName, this.id);
            undisposedAssociations
                .forEach(({ attrName, obj }) => this._owObjectLogger.debug(`Instance ${obj.instanceID} id ${obj.id} of class '${obj.objectName}' placed in attribute '${attrName}'`));
            this._owObjectLogger.groupEnd();
        }

        const loadingAssociations = Array.from(this._values.entries())
            .filter(([, a]) => a instanceof ObjAttrData && !a.loaded && a.object != null)
            .map(([attrName, a]) => ({ attrName, a: a as ObjAttrData, objPromise: (a as ObjAttrData).object }));

        loadingAssociations.forEach(({ objPromise }) => objPromise.then(obj => obj.dispose()));

        if (loadingAssociations.length > 0) {
            this._owObjectLogger.groupCollapsed(`Queueing dispose of ${loadingAssociations.length} associated instances of: `, this.instanceID, this.objectName, this.id);
            loadingAssociations
                .forEach(({ attrName, a }) => this._owObjectLogger.debug(`Instance ${a.instanceID} id ${a.id} of class '${a.className}' placed in attribute '${attrName}'`));
            this._owObjectLogger.groupEnd();
        }
    }
}

type CachedRightItem<T> = { time: number; getRightValuePromise: Promise<T>; cancellationToken: CancellationToken };

export class LazyLoadingData {
    public loaded: boolean;
}

export class ObjAttrData extends LazyLoadingData implements IAttrValue {
    public object: Promise<OWObject>;
    public className: string;
    public id: string;
    /**Instance ID načteného objektu (pokud už byl načten)*/
    public instanceID?: string;
}

class ObjAttrDataInternal extends ObjAttrData implements ILoadedAssociationInternal, IDisposable {
    public loadedInstance: OWObject;

    public dispose(): void {
        if (this.loadedInstance != null)
            this.loadedInstance.dispose();
        else if (this.object != null)
            this.object.then(o => o.dispose());

        this.loaded = false;
        this.object = null;
        this.loadedInstance = null;
    }
}

// musí zůstat interní, jde o implementační detail díky kterému se dá vnitřně dostat k načteným vazbám
interface ILoadedAssociationInternal {
    loadedInstance: OWObject;
}

/**
 * pomocna trida reprezentujici hodnotu, ktera se nacte az na pozadani
 * @ignore
 */
class DataOnRequest implements IEncAttrValue {
    public readonly isEncrypted: boolean;
    public readonly hasEncryptedValue: boolean = true;

    constructor(isEncrypted: boolean) {
        this.isEncrypted = isEncrypted;
    }

    public decrypt(_key: CryptoKey): Promise<boolean> {
        throw new Error("Not implemented");
    }
}

/**
 * Datové rozhraní pro hodnotu atributu odesílanou na server
 */
export interface ISetObjectAttrValue {
    name: string;
    value: string;
    isEncrypted?: boolean;
}

interface IRunningAttrUpdate {
    cts: CancellationTokenSource;
    task: Promise<OWObject>;
}

export class NullOWObject extends OWObject {
    public static createNullOWObject(): Promise<OWObject> {
        return Promise.resolve(new NullOWObject());
    }

    constructor() {
        super(undefined);
    }

    public override getValue(attributeName: string, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IAttrValue> {
        if (attributeName.toLowerCase() === "id") {
            return Promise.resolve(0);
        }
        return Promise.resolve(null);
    }

    public static override [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance?.__proto__?.constructor?.name === "NullOWObject";
    }
}
